import { useQuery, useQueryClient } from '@tanstack/react-query';

import { keys as productKeys } from 'common/queries/product-module';
import { useMemo } from 'react';
import * as productEndpoints from 'services/product/endpoints';

const productHierarchyShortDetail = {
  url: productEndpoints.GET_PRODUCT_HIERARCHIES_SHORT_DETAIL,
  all: ['pim', 'get-product-hierarchies-short-details'],
  one: (productId) => [
    ...productHierarchyShortDetail.all,
    { params: { productId: Number(productId) } },
  ],
};

const productHierarchyDetails = {
  url: productEndpoints.GET_PRODUCT_HIERARCHY_DETAILS,
  all: ['pim', 'get-product-hierarchy-details'],
  one: (hierarchyId) => [
    ...productHierarchyDetails.all,
    { params: { hierarchyId } },
  ],
};

const productMarketingAsset = {
  url: productEndpoints.GET_MARKETING_ASSETS,
  all: ['pim', 'get-marketing-assets'],
  one: ({ productId, isShowAll } = {}) => [
    ...productMarketingAsset.all,
    { params: { productId, isShowAll } },
  ],
};

const keys = {
  productHierarchyShortDetail,
  productHierarchyDetails,
  productMarketingAsset,
};

export const useGetProductHeaderBackground = ({ productId }) => {
  return useQuery({
    queryKey: productKeys.productHeaderBackground.one(productId),
  });
};

/**
 * * get product hierarchy short detail
 * @param {Object}
 * @returns
 */
export const useQueryProductHierarchyShortDetail = ({
  productId,
  ...options
} = {}) => {
  const queryClient = useQueryClient();

  const queryObject = useQuery({
    queryKey: keys.productHierarchyShortDetail.one(productId),
    enabled: !!productId,
    options,
  });

  const invalidateProductHierarchyShortDetail = (productId) => {
    queryClient.invalidateQueries({
      queryKey: productId
        ? keys.productHierarchyShortDetail.one(productId)
        : keys.productHierarchyShortDetail.all,
    });
  };

  const { data } = queryObject;

  return {
    ...queryObject,
    data: useMemo(() => data?.productHierarchies, [data]),
    invalidateProductHierarchyShortDetail,
  };
};

/**
 *  * get product marking assets
 * @param {Object}
 * @returns
 */
export const useQueryMarketingAssets = ({ productId, ...options } = {}) => {
  const queryClient = useQueryClient();

  const queryObject = useQuery({
    queryKey: keys.productMarketingAsset.one({
      productId,
      isShowAll: true,
    }),
    enabled: !!productId,
    options,
  });

  const invalidateMarketingAssets = (productId) => {
    queryClient.invalidateQueries({
      queryKey: productId
        ? keys.productMarketingAsset.one({ productId, isShowAll: true })
        : keys.productMarketingAsset.all,
    });
  };

  const { data } = queryObject;

  const formatData = useMemo(() => {
    return data?.gridData?.length > 0
      ? data.gridData.map((item) => ({
          ...item,
          id: item?.productImageLinkId,
        }))
      : [];
  }, [data]);

  return {
    ...queryObject,
    data: formatData,
    invalidateMarketingAssets,
  };
};

/**
 * * get product hierarchy details
 * @param {Object}
 * @returns
 */
export const useQueryProductHierarchyDetails = ({
  hierarchyId,
  ...options
} = {}) => {
  const queryClient = useQueryClient();

  const queryObject = useQuery({
    queryKey: keys.productHierarchyDetails.one(hierarchyId),
    enabled: !!hierarchyId,
    options,
  });

  const invalidateProductHierarchyDetails = (hierarchyId) => {
    queryClient.invalidateQueries({
      queryKey: hierarchyId
        ? keys.productHierarchyDetails.one(hierarchyId)
        : keys.productHierarchyDetails.all,
    });
  };

  return {
    ...queryObject,
    invalidateProductHierarchyDetails,
  };
};
